/**
 * Flow Optimizer — queue-row priority mutation. A queued element's priority
 * lives on its backing bank record (path / lander / offer), so we PATCH that and
 * refresh the queue. Voluum banks only (binom has no bank priority endpoint).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer } from '@trafficpulse/shared';

import api from '@/services/api';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from './useFlowOptimizerQueue';

/** Layer → Voluum bank REST collection that owns the element's priority. */
export const BANK_PATH: Record<FlowLayer, string> = {
  paths: 'path-banks',
  landers: 'lander-banks',
  offers: 'offers',
};

export interface SetQueuePriorityArgs {
  layer: FlowLayer;
  bankId: string;
  priority: number | null;
}

/** Set a queued element's priority via its Voluum bank record. */
export function useQueuePriority(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ layer, bankId, priority }: SetQueuePriorityArgs) =>
      api.patch(`/voluum/${BANK_PATH[layer]}/${bankId}`, { priority }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) }),
  });
}
